import { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { API, Storage } from "aws-amplify";
import { v4 as uuidv4 } from "uuid";
import { IconButton, Badge, Alert, Avatar } from "@mui/material";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import { updateAlumniProfile } from "../../graphql/mutations";
import awsconfig from "../../aws-exports";
import "./profilePicture.scss";

const propTypes = {
  profileId: PropTypes.string.isRequired,
  objectKey: PropTypes.string,
  avatar: PropTypes.elementType.isRequired,
  editable: PropTypes.bool.isRequired,
  alt: PropTypes.string.isRequired,
};

const defaultProps = {
  objectKey: "",
};

const bucketName = awsconfig.aws_user_files_s3_bucket;
const bucketRegion = awsconfig.aws_user_files_s3_bucket_region;

const ProfilePicture = ({ profileId, editable, objectKey, alt, avatar }) => {
  const [currentKey, setCurrentKey] = useState(objectKey);
  const [src, setSrc] = useState("");
  const [message, setMessage] = useState("");
  const StyledAvatar = avatar;

  useEffect(() => {
    if (currentKey) {
      setSrc(
        `https://${bucketName}.s3.${bucketRegion}.amazonaws.com/public/${currentKey}`
      );
    }
  }, [currentKey]);

  const isFileSizeOverLimit = (bytes) => {
    const megaBytes = (bytes / (1024 * 1024)).toFixed(2);
    return megaBytes > 5;
  };

  const handleFileUpload = async (e) => {
    setMessage("");
    const file = e.target.files[0];
    if (!file) return;
    if (isFileSizeOverLimit(file.size)) {
      setMessage("Maximum file size is 5 MB. Please upload a smaller file.");
      return;
    }
    try {
      const { key } = await Storage.put(
        `alumni-profile-pictures/${uuidv4()}-${file.name}`,
        file
      );
      if (currentKey) {
        await Storage.remove(currentKey);
      }

      const input = { id: profileId, profilePictureKey: key };
      await API.graphql({
        query: updateAlumniProfile,
        variables: { input },
      });

      setCurrentKey(key);
    } catch (error) {
      setMessage("Error uploading photo");
    }
  };

  return (
    <div className="profile-picture">
      {message && (
        <Alert className="alert" severity="error">
          {message}
        </Alert>
      )}
      {editable && profileId ? (
        <div className="avatar-badge">
          <Badge
            component="div"
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <label htmlFor="file-input">
                <input
                  type="file"
                  accept="image/*"
                  id="file-input"
                  hidden
                  onChange={handleFileUpload}
                />
                <IconButton
                  aria-label="upload profile picture"
                  component="span"
                >
                  <Avatar className="avatar-photo-icon">
                    <AddAPhotoOutlinedIcon />
                  </Avatar>
                </IconButton>
              </label>
            }
          >
            <StyledAvatar alt={alt} src={src}>
              {alt[0]}
            </StyledAvatar>
          </Badge>
        </div>
      ) : (
        <StyledAvatar alt={alt} src={src}>
          {alt[0]}
        </StyledAvatar>
      )}
    </div>
  );
};

ProfilePicture.propTypes = propTypes;
ProfilePicture.defaultProps = defaultProps;

export default ProfilePicture;
